// src/api/axiosConfig.js
import axios from "axios";
import { decodeJWTPayload, isTokenExpired } from "../utils/authUtils";
import { consumePendingIdempotencyKey } from "../utils/idempotencyKey";

const PROACTIVE_REFRESH_THRESHOLD_SECONDS = 25 * 60;

let isRefreshing = false;
let proactiveRefreshPromise = null;
let failedRequestsQueue = [];

let authAccessors = {
  getAccessToken: () => null,
  setAccessToken: () => {},
  refreshSession: null,
  logout: () => {},
  isRefreshRevoked: () => false,
};

let bootRefreshResolve = null;
let bootRefreshPromise = null;

export const initBootRefresh = () => {
  bootRefreshPromise = new Promise((resolve) => {
    bootRefreshResolve = resolve;
  });
};

export const resolveBootRefresh = () => {
  if (bootRefreshResolve) {
    bootRefreshResolve();
    bootRefreshResolve = null;
    bootRefreshPromise = null;
  }
};

initBootRefresh();

export const configureAxiosAuth = ({ getAccessToken, setAccessToken, refreshSession, logout, isRefreshRevoked }) => {
  authAccessors = {
    getAccessToken: typeof getAccessToken === "function" ? getAccessToken : authAccessors.getAccessToken,
    setAccessToken: typeof setAccessToken === "function" ? setAccessToken : authAccessors.setAccessToken,
    refreshSession: typeof refreshSession === "function" ? refreshSession : authAccessors.refreshSession,
    logout: typeof logout === "function" ? logout : authAccessors.logout,
    isRefreshRevoked: typeof isRefreshRevoked === "function" ? isRefreshRevoked : authAccessors.isRefreshRevoked,
  };
};

export const getAccessToken = () => authAccessors.getAccessToken?.() || null;

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:5001/api",
  withCredentials: true,
});

const PUBLIC_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/refresh",
  "/api/auth/logout",
  "/api/context/options",
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/context/options",
  "/login",
];

const getRequestPathname = (requestConfig = {}) => {
  const rawUrl = requestConfig.url || "";

  try {
    return new URL(rawUrl, api.defaults.baseURL).pathname;
  } catch {
    return rawUrl;
  }
};

const shouldBypassSessionRefresh = (requestConfig = {}) => {
  const pathname = getRequestPathname(requestConfig);

  return [...PUBLIC_ENDPOINTS, "/auth/close", "/api/auth/close"].some(
    (path) => pathname === path || pathname.endsWith(path)
  );
};

const isPublicEndpoint = (requestConfig = {}) => {
  const pathname = getRequestPathname(requestConfig);
  return PUBLIC_ENDPOINTS.some((path) => pathname === path || pathname.endsWith(path));
};

const getSecondsUntilExpiry = () => {
  const token = authAccessors.getAccessToken?.();
  if (!token) {
    return null;
  }

  const payload = decodeJWTPayload(token);
  if (!payload?.exp || typeof payload.exp !== "number") {
    return null;
  }

  return payload.exp - Math.floor(Date.now() / 1000);
};

const refreshAccessToken = async () => {
  if (authAccessors.isRefreshRevoked?.()) {
    const error = new Error("Refresh token is revoked");
    error.status = 401;
    throw error;
  }

  if (typeof authAccessors.refreshSession !== "function") {
    throw new Error("refreshSession is not configured");
  }

  const data = await authAccessors.refreshSession();
  return data?.accessToken || null;
};

const maybeProactivelyRefresh = (requestConfig = {}) => {
  if (shouldBypassSessionRefresh(requestConfig) || isRefreshing || proactiveRefreshPromise) {
    return;
  }

  const secondsUntilExpiry = getSecondsUntilExpiry();
  if (secondsUntilExpiry === null || secondsUntilExpiry > PROACTIVE_REFRESH_THRESHOLD_SECONDS) {
    return;
  }

  proactiveRefreshPromise = refreshAccessToken()
    .catch(() => null)
    .finally(() => {
      proactiveRefreshPromise = null;
    });
};

api.interceptors.request.use(
  (config) => {
    const token = authAccessors.getAccessToken?.();

    if (!isPublicEndpoint(config) && authAccessors.isRefreshRevoked?.()) {
      return Promise.reject(new Error("Session revoked"));
    }

    if (!isPublicEndpoint(config) && (!token || isTokenExpired(token))) {
      return Promise.reject(new Error("Request blocked: token missing or expired"));
    }

    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    const contextKind = localStorage.getItem("contextKind");
    const tenantId = localStorage.getItem("tenantId");
    const storeId = localStorage.getItem("storeId");
    const accountingClientId = localStorage.getItem("accountingClientId");

    if (!isPublicEndpoint(config)) {
      if (contextKind) config.headers["x-context-kind"] = contextKind;
      if (tenantId) config.headers["x-tenant-id"] = tenantId;
      if (storeId) config.headers["x-store-id"] = storeId;
      if (accountingClientId) config.headers["x-accounting-client-id"] = accountingClientId;
    } else {
      delete config.headers["x-context-kind"];
      delete config.headers["x-tenant-id"];
      delete config.headers["x-store-id"];
      delete config.headers["x-accounting-client-id"];
    }

    // Attach a per-submission Idempotency-Key on create requests guarded by
    // useGuardedMutation (see hooks/useGuardedMutation.js). The key is set just
    // before the call and consumed here so it never leaks onto an unrelated request.
    const method = (config.method || "").toLowerCase();
    if (method === "post" || method === "put") {
      const idempotencyKey = consumePendingIdempotencyKey();
      if (idempotencyKey) {
        config.headers["Idempotency-Key"] = idempotencyKey;
      }
    }

    if (config.url?.includes("/vat") || config.url?.includes("/vat-records")) {
      console.log("[AXIOS] Request headers:", {
        url: config.url,
        tenantId: config.headers["x-tenant-id"],
        contextKind: config.headers["x-context-kind"],
        accountingClientId: config.headers["x-accounting-client-id"],
        storeId: config.headers["x-store-id"],
        hasAuth: !!config.headers.Authorization,
      });
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    maybeProactivelyRefresh(response.config);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const shouldHandleUnauthorized =
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !shouldBypassSessionRefresh(originalRequest);

    if (shouldHandleUnauthorized) {
      if (bootRefreshPromise) {
        await bootRefreshPromise;
        const freshToken = authAccessors.getAccessToken?.();
        if (freshToken) {
          originalRequest._retry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          return api(originalRequest);
        }
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const accessToken = await refreshAccessToken();

        failedRequestsQueue.forEach((pendingRequest) => pendingRequest.resolve(accessToken));
        failedRequestsQueue = [];

        originalRequest.headers = originalRequest.headers || {};
        if (accessToken) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        failedRequestsQueue.forEach((pendingRequest) => pendingRequest.reject(refreshError));
        failedRequestsQueue = [];

        // Poller is the single authority for logout/redirect decisions.
        // Axios should only propagate the failure to callers.
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
